import { LiveAnnouncer } from '@angular/cdk/a11y';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  model,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import {
  MatChipEditedEvent,
  MatChipGrid,
  MatChipInput,
  MatChipInputEvent,
  MatChipRemove,
  MatChipRow,
} from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import {
  SiaSelectBoxDataService,
  SiaTooltipDirective,
  SiaTranslatePipe,
} from '@sia/shared';
import { ChipsFieldOptions } from '../../interfaces/chips-field-options';
import { SiaBaseEditorSignalComponent } from '../sia-base-editor/sia-base-editor-signal.component';

@Component({
  selector: 'chips-input',
  imports: [
    MatFormField,
    MatLabel,
    MatChipGrid,
    MatChipRow,
    MatChipInput,
    MatIcon,
    MatChipRemove,
    SiaTranslatePipe,
    SiaTooltipDirective,
    MatAutocompleteModule,
    FormsModule,
  ],
  templateUrl: './chips-input.component.html',
  styleUrl: './chips-input.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipsInputComponent extends SiaBaseEditorSignalComponent {
  readonly announcer = inject(LiveAnnouncer);
  readonly addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  readonly currentValue = model('');
  readonly placeholder = input<string>('set placeholder');
  readonly view = input<string>('');
  readonly regExp = input<RegExp | undefined>(undefined);
  readonly typeOfFilterResearch = input<'email' | ''>('');
  readonly inputHeight = input<string | number>('');
  readonly chipsFieldOptions = input<ChipsFieldOptions>({
    key: '',
    foreignKey: '',
  });
  readonly height = computed(() => {
    const value = this.inputHeight();
    switch (typeof value) {
      case 'number':
        return `${value}px`;
      case 'string':
        return value;
      default:
        return '';
    }
  });
  readonly dataList = signal<string[]>([]);
  #siaWidgetDataService = inject(SiaSelectBoxDataService);
  #dialog = inject(MatDialog);

  listValue = model<string[]>([]);
  #addTimeout: any;
  selectedFired: boolean = false;
  #selectedTimeout: any;
  @ViewChild('chipInputAutocomplete') chipInputMenu?: ElementRef;
  @ViewChild('chipInput') chipInput?: ElementRef<HTMLInputElement>;

  readonly filteredValue = computed(() => {
    const currentValue = this.currentValue().toLowerCase();
    const dataList = this.dataList();
    const typeOfFilterResearch = this.typeOfFilterResearch();
    return currentValue
      ? this.dataList().filter((username) => {
          if (this.typeOfFilterResearch() === 'email') {
            let elem = username.split('@')[0];
            return elem.toLowerCase().includes(currentValue);
          } else {
            return username.toLowerCase().includes(currentValue);
          }
        })
      : this.dataList().slice();
  });

  async ngOnInit() {
    await this.#siaWidgetDataService
      .loadFullData(
        this.chipsFieldOptions().key,
        this.chipsFieldOptions().foreignKey,
        false,
      )
      .then((res) => {
        this.dataList.set(
          res.map((item: any) => {
            return item.description;
          }),
        );
      });
  }

  add(event: MatChipInputEvent): void {
    if (this.selectedFired) {
      return;
    }
    clearTimeout(this.#addTimeout);
    this.#addTimeout = setTimeout(() => {
      const value = (event.value || '').trim();
      if (value) {
        this.listValue.update((items) => {
          if (!Array.isArray(items)) {
            return [value];
          }
          return [...items, value];
        });
      }
      event.chipInput!.clear();
    }, 100);
  }
  #setValueChipInput() {}
  remove(value: string): void {
    this.listValue.update((item) => {
      const index = item.indexOf(value);
      if (index < 0) {
        return item;
      }
      item.splice(index, 1);
      this.announcer.announce(`Removed ${item}`);
      return [...item];
    });
  }

  onTabKey(event: any): void {
    const currentInput = this.currentValue().trim();
    if (currentInput && this.filteredValue().length > 0) {
      event.preventDefault();
      const firstOption = this.filteredValue()[0];
      this.listValue.update((items) => {
        if (!Array.isArray(items)) {
          return [firstOption];
        }
        return [...items, firstOption];
      });
      this.currentValue.set('');
      if (this.chipInput?.nativeElement) {
        this.chipInput.nativeElement.value = '';
      }
    }
    // If input is empty, let Tab work normally to navigate to next element
  }
  edit(value: string, event: MatChipEditedEvent) {
    const eventValue = event.value.trim();
    if (!eventValue) {
      this.remove(value);
      return;
    }
    this.listValue.update((item) => {
      const index = item.indexOf(value);
      if (index >= 0) {
        item[index] = eventValue;
        return [...item];
      }
      return item;
    });
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.selectedFired = true;
    clearTimeout(this.#addTimeout);
    clearTimeout(this.#selectedTimeout);
    if (event.option.viewValue) {
      this.listValue.update((items) => {
        if (!Array.isArray(items)) {
          return [event.option.viewValue];
        }
        return [...items, event.option.viewValue];
      });
    }
    this.currentValue.set('');
    event.option.deselect();
    this.#selectedTimeout = setTimeout((x: any) => {
      this.selectedFired = false;
    }, 100);
  }

  ngOnDestroy() {}
}
